/**
 * Wall-parameter-space machinery for the Style-It decor planners: which spans of
 * a wall are blocked (furniture + openings), what the largest open span is, and
 * which wall to feature. Everything works in "wall param" space — distance along
 * the wall base line, p ∈ [0, wallLen] — so the emitters can reason in 1-D.
 *
 * This module is the only consumer of the optimizer's `freeZonesFromOccupied`
 * (plain 1-D interval math, no roomData shape).
 */

const {
  freeZonesFromOccupied,
} = require('../../dorm-optimizer-v2/placement/geometry-gaps');
const { footprintFor } = require('../../geometry/objectFootprint');
const {
  getFloorY,
  wallAxesFor,
  pointToSegmentDistanceXZ,
  findNearestWall,
} = require('../dormDecorGeometry');
const {
  PHOTO_WALL_EYE_HEIGHT_M,
  WALL_TOUCH_DISTANCE_M,
  FRAME_HEIGHT_M,
} = require('./constants');

/**
 * Project a world point ({x,z}) to wall-parameter space p ∈ [0, wallLen] along
 * the wall direction: p = (pt − axes.start)·wallDir.
 */
function projectToParam(pt, axes) {
  return (
    (pt.x - axes.start.x) * axes.wallDir.x +
    (pt.z - axes.start.z) * axes.wallDir.z
  );
}

/**
 * Loft/bunk beds occupy the wall at frame height even though their stored
 * `dimensions.y` is only the bed-frame box — so identify them by type and block
 * their wall regardless of the (unreliable) bbox height.
 */
function isLoftOrBunkBed(type) {
  const t = (type || '').toLowerCase();
  return t.includes('loft') || t.includes('bunk');
}

/**
 * Every door, window and doorless opening the room carries.
 *
 * ONE list, because the two passes below used to spell it out separately and
 * both spelled it `doors + windows` — `room.openings` was never read, so a
 * doorless opening counted as bare wall and staging hung a framed print in the
 * middle of one (Golden West kitchen, 2026-11-02; 3 corpus kitchens and 8
 * bathroom pieces). Everything here blocks a wall the same way: nothing hangs
 * across a hole in it.
 */
function wallOpeningsOf(room) {
  return [
    ...(room?.doors || []),
    ...(room?.windows || []),
    ...(room?.openings || []),
  ];
}

/**
 * Is this opening on this ROOM wall? Named directly by `parentWallIds`, or —
 * since openings can reference the STRUCTURE wall (`sw-*`), which no room wall
 * copy (`rw-*`) matches — the wall nearest its centre.
 *
 * Either answer counts. A corner opening is near two walls and blocks a little
 * of both; that only ever costs a piece, never hangs one in a hole.
 */
function isOpeningOnWall(opening, wall, walls, endpointsById) {
  if ((opening?.parentWallIds || []).includes(wall.id)) return true;
  if (!opening?.center) return false;
  const nearest = findNearestWall(opening.center, walls, endpointsById);
  return Boolean(nearest && nearest.id === wall.id);
}

/** Set of ROOM wall ids that carry any door, window or opening. */
function wallsWithOpenings(room, walls, endpointsById) {
  const ids = new Set();
  for (const wall of walls || []) {
    if (
      wallOpeningsOf(room).some((o) =>
        isOpeningOnWall(o, wall, walls, endpointsById)
      )
    )
      ids.add(wall.id);
  }
  return ids;
}

/**
 * Compute the blocked wall-parameter intervals on the feature wall — the spans
 * a photo cluster must avoid. Two sources, both projected to param space:
 *
 *   1. Furniture that (a) touches the wall (min corner-to-segment distance ≤
 *      WALL_TOUCH_DISTANCE_M) AND (b) reaches the frame band (its top
 *      `center.y + dims.y/2 >= eyeY − frameHalfH`). The HEIGHT GATE (Gotcha #4):
 *      a low desk/dresser is intentionally NOT blocked so a gallery can hang
 *      above it; a tall wardrobe/loft IS. Footprint corners come from
 *      `objectFootprint` (DRY — no hand-rolled rotation).
 *   2. Each door, window and doorless opening on this wall
 *      (`isOpeningOnWall`): center ± half-width, or the WHOLE wall when its
 *      position is unknown. Not height-gated: every corpus opening starts at
 *      the floor (bottom −0.08…0.09 m), so all of them reach the band.
 *
 * @returns {Array<{start:number,end:number}>} intervals in [0, wallLen]
 */
function computeWallBlockedParams(feature, room, endpointsById) {
  const { wall, axes } = feature;
  const wallLen = axes.wallLen;
  const eyeY = getFloorY(room) + PHOTO_WALL_EYE_HEIGHT_M;
  const frameHalfH = FRAME_HEIGHT_M / 2;
  const bandBottom = eyeY - frameHalfH;
  const bandTop = eyeY + frameHalfH;

  const blocked = [];

  // 1. Furniture footprints (height-gated).
  for (const obj of room?.objects || []) {
    const center = obj?.center;
    const dims = obj?.dimensions;
    if (!center || !dims) continue;

    // Height gate: block when the object reaches the frame band. Loft/bunk beds
    // are a special case — their stored `dimensions.y` is only the bed-frame box
    // (~0.9m) and does NOT capture the elevated mattress + posts that actually
    // occupy the wall up to ~1.9m. Trust the TYPE for those, not the bbox height,
    // or decor lands on the wall a loft is pushed against.
    const top = center.y + (dims.y || 0) / 2;
    if (top < bandBottom && !isLoftOrBunkBed(obj?.type)) continue;
    // ...and gated at the TOP too: an object entirely ABOVE the band is not
    // in the way of anything hung in it. The gate used to ask only whether
    // an object reached UP to the band, so a ceiling light 8 ft up and
    // within touch distance of two walls cut both of them at its position
    // — in one powder room that left the sink end of every wall with no
    // run a towel ring could take, and the ring was dropped.
    const bottom = center.y - (dims.y || 0) / 2;
    if (bottom > bandTop && !isLoftOrBunkBed(obj?.type)) continue;

    const corners = footprintFor(
      center,
      dims.x || 0,
      dims.z || 0,
      obj?.rotation?.y || 0
    );

    // Touching the wall? nearest footprint corner within WALL_TOUCH_DISTANCE_M.
    let minDist = Infinity;
    for (const c of corners) {
      const d = pointToSegmentDistanceXZ(c, axes.start, axes.end);
      if (d < minDist) minDist = d;
    }
    if (minDist > WALL_TOUCH_DISTANCE_M) continue;

    // Project corners → param span, clamped to the wall.
    let minP = Infinity;
    let maxP = -Infinity;
    for (const c of corners) {
      const p = projectToParam(c, axes);
      if (p < minP) minP = p;
      if (p > maxP) maxP = p;
    }
    const start = Math.max(0, Math.min(minP, maxP));
    const end = Math.min(wallLen, Math.max(minP, maxP));
    if (end > start) blocked.push({ start, end });
  }

  // 2. Doors, windows and openings on this wall.
  const walls = room?.walls || [];
  for (const opening of wallOpeningsOf(room)) {
    if (!isOpeningOnWall(opening, wall, walls, endpointsById)) continue;
    const width = opening?.dimensions?.x;
    // On this wall, but WHERE is unknown: a door whose structure copy did
    // not resolve (`withResolvedOpeningsOnly` keeps those for their wall
    // attribution). It used to be skipped, which left the whole wall free
    // and anything could hang across the doorway. Unknown position blocks
    // the whole wall — a piece lost beats a piece in a hole.
    if (!opening?.center || !(width > 0)) {
      blocked.push({ start: 0, end: wallLen });
      continue;
    }
    const p = projectToParam(opening.center, axes);
    const start = Math.max(0, p - width / 2);
    const end = Math.min(wallLen, p + width / 2);
    if (end > start) blocked.push({ start, end });
  }

  return blocked;
}

/**
 * Largest contiguous FREE interval on a wall at frame height, or null. "Free" =
 * not blocked by furniture (height-gated) and not spanned by a door, window or
 * opening — the same blocking the photo wall avoids (`computeWallBlockedParams`
 * + `freeZonesFromOccupied`). This is what keeps wall decor on genuinely open
 * walls and off loft/wardrobe walls.
 */
/**
 * EVERY free interval on a wall at frame height, longest first (each
 * `{ start, end, length }`), by the same blocking as `largestFreeSpanOnWall`.
 * A wall with a toilet at one end and a vanity at the other has two usable
 * runs, and which one a prop wants depends on the prop: a towel ring wants
 * the one beside the basin even when it is the shorter by a few centimetres.
 */
function freeSpansOnWall(wall, axes, room, endpointsById) {
  const blocked = computeWallBlockedParams({ wall, axes }, room, endpointsById);
  return freeZonesFromOccupied(blocked, axes.wallLen)
    .map((z) => ({ start: z.start, end: z.end, length: z.end - z.start }))
    .filter((z) => z.length > 0)
    .sort((a, b) => b.length - a.length);
}

function largestFreeSpanOnWall(wall, axes, room, endpointsById) {
  return freeSpansOnWall(wall, axes, room, endpointsById)[0] || null;
}

/**
 * Choose the FEATURE wall for wall decor: the wall with the largest contiguous
 * OPEN span at frame height (furniture- AND opening-aware), tie-broken by wall
 * length. Falls back to the longest wall overall only when no wall has any open
 * span (downstream sub-span logic then skips gracefully).
 *
 * This replaces the old "longest wall without an opening" rule, which selected
 * loft/wardrobe walls that carry no window but are fully furniture-blocked — the
 * cause of tapestries/mirrors landing on top of a bed.
 */
function selectFeatureWall(walls, endpointsById, room) {
  let best = null; // most open span
  let longest = null; // fallback when nothing is open
  for (const wall of walls) {
    const info = wallAxesFor(wall, endpointsById);
    if (!info) continue;
    const axes = info.axes;
    if (!longest || axes.wallLen > longest.axes.wallLen) {
      longest = { wall, axes };
    }
    const free = largestFreeSpanOnWall(wall, axes, room, endpointsById);
    const freeLen = free ? free.length : 0;
    if (
      !best ||
      freeLen > best.freeLen ||
      (freeLen === best.freeLen && axes.wallLen > best.axes.wallLen)
    ) {
      best = { wall, axes, freeLen };
    }
  }
  return best && best.freeLen > 0 ? best : longest;
}

/**
 * Pick a distinct AUTO wall for wall art that shouldn't share the feature wall:
 * the longest wall that isn't the feature wall. Returns { wall, axes } or null
 * when the room has no other usable wall (caller falls back to the feature wall).
 */
function selectAutoWall(walls, endpointsById, feature, room) {
  let best = null;
  for (const wall of walls) {
    if (feature?.wall?.id && wall.id === feature.wall.id) continue;
    const info = wallAxesFor(wall, endpointsById);
    if (!info) continue;
    const free = largestFreeSpanOnWall(wall, info.axes, room, endpointsById);
    const freeLen = free ? free.length : 0;
    if (!best || freeLen > best.freeLen) {
      best = { wall, axes: info.axes, freeLen };
    }
  }
  // Only return a wall that actually has open span; else caller falls back to
  // the feature wall (which itself skips if it has no room).
  return best && best.freeLen > 0 ? best : null;
}

module.exports = {
  freeSpansOnWall,
  projectToParam,
  isLoftOrBunkBed,
  wallOpeningsOf,
  isOpeningOnWall,
  wallsWithOpenings,
  computeWallBlockedParams,
  largestFreeSpanOnWall,
  selectFeatureWall,
  selectAutoWall,
};
